/**
 * 星点层（ADR-0004）：一层里所有的星一次实例化绘制，每颗星一个面片。
 * 照片星和装饰粒子（爆炸后成为背景天幕）各用一层。
 *
 * 入场时（uBang < 1）每颗星先待在爱心上（aHeart），随心跳鼓起、坍缩时扭转着收向中心，
 * 爆炸时沿 aBurst 冲出去，再落回自己的位置 aPos；aOrder 决定落定的先后。做法同原型的 GALAXY_VERT。
 */
import * as THREE from 'three';

const VERT = /* glsl */ `
attribute vec3 aPos;
attribute vec3 aColor;
attribute float aSize;
attribute float aBorn;
attribute float aSeed;
attribute vec4 aHeart;
attribute vec3 aBurst;
attribute float aOrder;
uniform float uTime;
uniform float uViewH;
uniform float uMinPx;
uniform float uMaxPx;
uniform float uTwinkle;
uniform float uOpacity;
uniform float uBang;
uniform float uCollapse;
uniform float uBeat;
uniform float uSway;
uniform float uHeartSize;
uniform float uBurstK;
uniform float uStagger;
uniform float uHeartStar;
uniform float uHeartGain;
uniform float uRestGain;
varying vec3 vColor;
varying vec2 vUv;
varying float vGain;
varying float vFlash;

float hash1(float n) { return fract(sin(n) * 43758.5453); }

void main() {
  float age = uTime - aBorn;
  float appear = clamp(age / 0.9, 0.0, 1.0);
  float flash = age > 0.0 ? exp(-age * 0.8) : 0.0;
  vec3 p = aPos;
  vec3 col = aColor;
  float heartK = 0.0, flare = 0.0, kindA = 1.0;
  if (uBang < 1.0) {
    appear = 1.0;
    flash = 0.0;
    vec3 h = aHeart.xyz;
    float kind = aHeart.w;
    float cyc = fract(uTime * (0.07 + aSeed * 0.09) + aSeed * 7.0), aura = step(2.5, kind);
    h *= 1.0 + aura * cyc * 0.55;                                      // 外圈光尘不断向外飘散
    h *= 1.0 + uBeat * (0.09 + 0.09 * aSeed) * (1.0 - 0.6 * aura);     // 心跳：随节拍向外鼓起
    float q = floor(uTime * 24.0);
    h += (vec3(hash1(aSeed * 91.3 + q), hash1(aSeed * 47.1 + q * 1.3), hash1(aSeed * 13.9 + q * 1.7)) - 0.5) * 0.01;
    float c = uCollapse * uCollapse, tw = c * 3.0 * (0.4 + aSeed);     // 坍缩：带着扭转收向中心
    h.xz = mat2(cos(tw), sin(tw), -sin(tw), cos(tw)) * h.xz;
    h *= mix(1.0, 0.05 + 0.06 * aSeed, c);
    h.xz = mat2(cos(uSway), sin(uSway), -sin(uSway), cos(uSway)) * h.xz; // 轻轻转身，看得出是立体的
    float b = clamp((uBang - aOrder * uStagger) / (1.0 - uStagger), 0.0, 1.0), settle = smoothstep(0.1, 1.0, b);
    flare = sin(3.14159265 * pow(b, 0.5)) * (1.0 - b);                  // 爆炸：先猛冲出去，再落回原位
    p = mix(h * uHeartSize, aPos, settle) + aBurst * flare * uBurstK;
    heartK = 1.0 - settle;
    vec3 hc = kind < 0.5 ? vec3(1.0, 0.42, 0.6) : kind < 1.5 ? vec3(0.95, 0.24, 0.46) : kind < 2.5 ? vec3(0.66, 0.1, 0.28) : vec3(1.0, 0.78, 0.6);
    col = mix(aColor, hc * (0.8 + 0.4 * aSeed), heartK) * (1.0 + c * 1.5);
    kindA = mix(1.0, kind < 0.5 ? 0.58 : kind < 1.5 ? 0.42 : kind < 2.5 ? 0.26 : 0.7 * (1.0 - cyc), heartK);
  }
  if (appear <= 0.0 || uOpacity <= 0.0) {
    // 还没点亮或整层隐藏：放到裁剪范围外，不花填充
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  float dist = max(-mv.z, 0.001);
  float twinkle = 1.0 - uTwinkle * (0.18 + 0.18 * sin(uTime * (0.6 + aSeed * 1.8) + aSeed * 61.0));
  // 在爱心上时统一用小一号的粒子；爆炸飞行中放大一些
  float d = mix(aSize * (1.0 + 2.5 * flash), uHeartStar * (0.7 + 0.6 * aSeed), heartK) * (1.0 + 1.5 * flare);
  // 每个世界单位在屏幕上占几个像素；太小的星按最小像素画、亮度相应降一点，太大的封顶
  float pxPerUnit = uViewH * 0.5 * projectionMatrix[1][1] / dist;
  float px = d * pxPerUnit;
  float gain = 1.0;
  if (px < uMinPx) {
    gain = mix(1.0, px / uMinPx, 0.75);
    d = uMinPx / pxPerUnit;
  } else if (px > uMaxPx) {
    d = uMaxPx / pxPerUnit;
  }
  mv.xy += position.xy * d;
  gl_Position = projectionMatrix * mv;
  vUv = position.xy * 2.0;
  vColor = col;
  // 离镜头太近的粒子淡掉，免得飞过镜头时糊满屏幕
  // 在爱心上亮一些，配合入场时低阈值的辉光让心发光；落定后按层调亮度（装饰粒子当天幕时要亮一些才看得见）
  vGain = twinkle * appear * gain * kindA * uOpacity * (1.0 + 2.0 * flare) * smoothstep(1.0, 8.0, dist) * mix(uRestGain, uHeartGain, heartK);
  vFlash = flash * appear;
}
`;

const FRAG = /* glsl */ `
varying vec3 vColor;
varying vec2 vUv;
varying float vGain;
varying float vFlash;

void main() {
  float r2 = dot(vUv, vUv);
  if (r2 >= 1.0) discard;
  float core = exp(-r2 * 34.0);
  float halo = exp(-r2 * 5.5) * (1.0 - r2);
  // 核心偏向星自己的颜色：很多星叠在一起时不至于直接冲成白色
  vec3 c = vColor * (halo * 0.7 + core * 0.5) + mix(vColor, vec3(1.0, 0.97, 0.92), 0.6) * core * 0.7;
  c += vec3(1.0, 0.85, 0.7) * vFlash * (halo + core) * 0.9;
  gl_FragColor = vec4(c * vGain, 1.0);
  #include <colorspace_fragment>
}
`;

export interface StarInit {
  x: number;
  y: number;
  z: number;
  color: THREE.Color;
  size: number;
  born: number;
  seed: number;
  /** 入场时在爱心上的位置（心形局部坐标）和种类 */
  heart: { x: number; y: number; z: number; kind: number };
  burst: { x: number; y: number; z: number };
  /** 0–1，入场爆炸后落定的先后 */
  order: number;
}

interface Attrs {
  pos: THREE.InstancedBufferAttribute;
  color: THREE.InstancedBufferAttribute;
  size: THREE.InstancedBufferAttribute;
  born: THREE.InstancedBufferAttribute;
  seed: THREE.InstancedBufferAttribute;
  heart: THREE.InstancedBufferAttribute;
  burst: THREE.InstancedBufferAttribute;
  order: THREE.InstancedBufferAttribute;
}

const QUAD = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
const ATTR_NAMES: Record<keyof Attrs, string> = {
  pos: 'aPos',
  color: 'aColor',
  size: 'aSize',
  born: 'aBorn',
  seed: 'aSeed',
  heart: 'aHeart',
  burst: 'aBurst',
  order: 'aOrder',
};
const ITEM_SIZE: Record<keyof Attrs, number> = { pos: 3, color: 3, size: 1, born: 1, seed: 1, heart: 4, burst: 3, order: 1 };

export class StarField {
  readonly mesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.ShaderMaterial>;
  readonly uniforms = {
    uTime: { value: 0 },
    uViewH: { value: 800 },
    uMinPx: { value: 2.4 },
    uMaxPx: { value: 90 },
    uTwinkle: { value: 1 },
    uOpacity: { value: 1 },
    uBang: { value: 1 },
    uCollapse: { value: 0 },
    uBeat: { value: 0 },
    uSway: { value: 0 },
    uHeartSize: { value: 16 },
    uBurstK: { value: 115 },
    uStagger: { value: 0.12 },
    uHeartStar: { value: 0.36 },
    uHeartGain: { value: 1.3 },
    uRestGain: { value: 1 },
  };
  count = 0;
  /** 最多画多少颗（画质降档时少画一些装饰粒子）；顺序是随机的，少画尾巴等于均匀地稀疏 */
  private limit = Number.POSITIVE_INFINITY;
  private capacity = 0;
  private attrs!: Attrs;
  private dirtyFrom = Number.POSITIVE_INFINITY;
  private dirtyTo = 0;

  constructor(capacity = 1024) {
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.allocate(capacity), material);
    // 包围球只是一个小面片，不能按它做视锥剔除
    this.mesh.frustumCulled = false;
  }

  get positions(): Float32Array {
    return this.attrs.pos.array as Float32Array;
  }
  get sizes(): Float32Array {
    return this.attrs.size.array as Float32Array;
  }
  get borns(): Float32Array {
    return this.attrs.born.array as Float32Array;
  }
  /** 线性空间的颜色，每颗星三个数 */
  get colors(): Float32Array {
    return this.attrs.color.array as Float32Array;
  }

  private allocate(capacity: number): THREE.InstancedBufferGeometry {
    const old = this.attrs as Attrs | undefined;
    const next = {} as Attrs;
    for (const key of Object.keys(ITEM_SIZE) as (keyof Attrs)[]) {
      const itemSize = ITEM_SIZE[key];
      const arr = new Float32Array(capacity * itemSize);
      if (old) arr.set((old[key].array as Float32Array).subarray(0, this.count * itemSize));
      const a = new THREE.InstancedBufferAttribute(arr, itemSize);
      a.setUsage(THREE.DynamicDrawUsage);
      next[key] = a;
    }
    this.attrs = next;
    this.capacity = capacity;
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(QUAD, 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    for (const key of Object.keys(ATTR_NAMES) as (keyof Attrs)[]) geo.setAttribute(ATTR_NAMES[key], this.attrs[key]);
    geo.instanceCount = Math.min(this.count, this.limit);
    return geo;
  }

  /** 清空并预留空间 */
  reset(capacity: number): void {
    this.count = 0;
    if (capacity > this.capacity) this.grow(capacity);
    this.mesh.geometry.instanceCount = 0;
  }

  private grow(min: number): void {
    const old = this.mesh?.geometry;
    const geo = this.allocate(Math.max(min, Math.ceil(this.capacity * 1.5), 1024));
    if (this.mesh) {
      this.mesh.geometry = geo;
      old?.dispose();
    }
    this.dirtyFrom = 0;
    this.dirtyTo = this.count;
  }

  push(s: StarInit): number {
    if (this.count >= this.capacity) this.grow(this.count + 1);
    const i = this.count++;
    const a = this.attrs;
    (a.pos.array as Float32Array).set([s.x, s.y, s.z], i * 3);
    (a.color.array as Float32Array).set([s.color.r, s.color.g, s.color.b], i * 3);
    (a.size.array as Float32Array)[i] = s.size;
    (a.born.array as Float32Array)[i] = s.born;
    (a.seed.array as Float32Array)[i] = s.seed;
    (a.heart.array as Float32Array).set([s.heart.x, s.heart.y, s.heart.z, s.heart.kind], i * 4);
    (a.burst.array as Float32Array).set([s.burst.x, s.burst.y, s.burst.z], i * 3);
    (a.order.array as Float32Array)[i] = s.order;
    this.dirtyFrom = Math.min(this.dirtyFrom, i);
    this.dirtyTo = Math.max(this.dirtyTo, i + 1);
    this.mesh.geometry.instanceCount = Math.min(this.count, this.limit);
    return i;
  }

  setLimit(n: number): void {
    this.limit = Math.max(0, n);
    this.mesh.geometry.instanceCount = Math.min(this.count, this.limit);
  }

  setBorn(i: number, born: number): void {
    (this.attrs.born.array as Float32Array)[i] = born;
    this.dirtyFrom = Math.min(this.dirtyFrom, i);
    this.dirtyTo = Math.max(this.dirtyTo, i + 1);
  }

  /** 每帧调一次：只上传改过的那一段 */
  commit(): void {
    if (this.dirtyFrom >= this.dirtyTo) return;
    for (const a of Object.values(this.attrs)) {
      a.clearUpdateRanges();
      a.addUpdateRange(this.dirtyFrom * a.itemSize, (this.dirtyTo - this.dirtyFrom) * a.itemSize);
      a.needsUpdate = true;
    }
    this.dirtyFrom = Number.POSITIVE_INFINITY;
    this.dirtyTo = 0;
  }
}
